import {
  Box,
  Typography,
  useMediaQuery,
  CircularProgress,
  Dialog,
  Button
} from "@mui/material";
import React, { useState, useRef, useCallback } from "react";
import Header from "../../Components/Common/Header";
import Footer from "../../Components/Common/Footer";
import FloatingSearchBar from "../../Components/Common/FloatingSearchBar";
import axios from "axios";
import config from "../../config/config";
import { motion, AnimatePresence } from "framer-motion";
import ImageSimilarProductCard from "../../Components/ImageSearch/ImageSimilarProductCard";
import { CameraAlt, CloudUpload, Close, Search, PhotoCamera, FlipCameraIos, Crop, SearchOff, AutoAwesome, ShoppingBag, ArrowForward, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { logUserAction } from "../../utils/analytics";
import { appendUtmTags } from "../../utils/urlUtils";

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Strip markdown backslash escapes (e.g. \- → -, \. → .)
 */
const stripMarkdownEscapes = (text) => {
  if (!text) return text;
  return text.replace(/\\/g, "");
};

/**
 * Parse the AI Mode markdown response into structured fields.
 * Expected format:
 *   • Brand: [value]
 *   • Product Name: [value]
 *   • Link: [StoreName](URL)
 *   [short description]
 */
const parseAiResponse = (markdown) => {
  if (!markdown) return null;

  // Clean the markdown of backslash escapes first
  const cleaned = stripMarkdownEscapes(markdown);

  const brandMatch = cleaned.match(/•\s*Brand:\s*(.+)/i);
  const nameMatch = cleaned.match(/•\s*Product\s*Name:\s*(.+)/i);

  // Try multiple link patterns:
  // 1. Standard markdown: [text](url)
  // 2. Bare URL on a Link: line
  // 3. Any bare https:// URL in the text
  let linkUrl = null;
  let linkText = null;

  const mdLinkMatch = cleaned.match(/\[([^\]]*)\]\((https?:\/\/[^)]+)\)/);
  if (mdLinkMatch) {
    linkText = mdLinkMatch[1];
    linkUrl = mdLinkMatch[2];
  } else {
    // Try bare URL on the Link line
    const bareLinkMatch = cleaned.match(/•\s*Link:\s*(https?:\/\/\S+)/i);
    if (bareLinkMatch) {
      linkUrl = bareLinkMatch[1];
    } else {
      // Try any URL in the text
      const anyUrlMatch = cleaned.match(/(https?:\/\/[^\s)]+)/);
      if (anyUrlMatch) {
        linkUrl = anyUrlMatch[1];
      }
    }
  }

  // Description: everything after the last bullet or link line
  const lines = cleaned.split("\n").filter((l) => l.trim());
  const descriptionLines = lines.filter(
    (l) =>
      !l.match(/•\s*Brand:/i) &&
      !l.match(/•\s*Product\s*Name:/i) &&
      !l.match(/•\s*Link:/i) &&
      !l.match(/^\[.*\]\(http/) &&
      !l.match(/^https?:\/\//)
  );

  return {
    brand: brandMatch ? brandMatch[1].trim() : null,
    productName: nameMatch ? nameMatch[1].trim() : null,
    linkUrl: linkUrl,
    linkText: linkText,
    description:
      descriptionLines.length > 0
        ? descriptionLines.join(" ").trim()
        : null,
    raw: cleaned,
  };
};

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

// ─── Component ──────────────────────────────────────────────────────────────

const ImageSearch = () => {
  const navigate = useNavigate();
  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const mediumScreen = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "lg")
  );

  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [originalPreviewUrl, setOriginalPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Results
  const [aiResult, setAiResult] = useState(null); // parsed AI mode result
  const [similarProducts, setSimilarProducts] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [uploadedS3Url, setUploadedS3Url] = useState(null);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [dots, setDots] = useState("");
  const [cameraMode, setCameraMode] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");

  // Crop State
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const imgRef = useRef(null);

  const handleCropApply = useCallback(() => {
    if (!completedCrop || !imgRef.current) {
      setIsCropping(false);
      return;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Check if the crop has any dimensions
    if (!completedCrop.width || !completedCrop.height) {
      setIsCropping(false);
      return;
    }

    const pixelRatio = window.devicePixelRatio;
    canvas.width = completedCrop.width * scaleX * pixelRatio;
    canvas.height = completedCrop.height * scaleY * pixelRatio;
    const ctx = canvas.getContext('2d');

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });

        // Clear previous blob URL
        if (previewUrl && previewUrl !== originalPreviewUrl && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }

        const newPreviewUrl = URL.createObjectURL(file);
        setSelectedFile(file);
        setPreviewUrl(newPreviewUrl);
        setIsCropping(false);
        setCrop(undefined);
        setCompletedCrop(null);

        // Update session storage
        const stateToSave = {
          previewUrl: newPreviewUrl,
          originalPreviewUrl: originalPreviewUrl,
          hasSearched: false,
          aiResult: null,
          similarProducts: []
        };
        sessionStorage.setItem("imageSearchState", JSON.stringify(stateToSave));
      },
      'image/jpeg',
      0.95
    );
  }, [completedCrop, previewUrl]);

  // Dots animation for loading
  React.useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + "." : ""));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [loading]);

  // ─── File Handling ──────────────────────────────────────────────────

  const handleFileSelect = useCallback((file) => {
    setError(null);

    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Please upload a JPEG, PNG, or WebP image.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("Image is too large. Maximum size is 15MB.");
      return;
    }

    const newPreviewUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(newPreviewUrl);
    setOriginalPreviewUrl(newPreviewUrl);
    setHasSearched(false);
    setAiResult(null);
    setSimilarProducts([]);
    setCrop(undefined);
    setIsCropping(true);

    // Save preview to session storage
    const stateToSave = {
      previewUrl: newPreviewUrl,
      originalPreviewUrl: newPreviewUrl,
      hasSearched: false,
      aiResult: null,
      similarProducts: [],
      uploadedS3Url: null
    };
    sessionStorage.setItem("imageSearchState", JSON.stringify(stateToSave));
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer?.files?.[0];
      handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const removeFile = useCallback(() => {
    setSelectedFile(null);
    if (previewUrl && previewUrl !== originalPreviewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    if (originalPreviewUrl && originalPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(originalPreviewUrl);
    }
    setPreviewUrl(null);
    setOriginalPreviewUrl(null);
    setError(null);
    setHasSearched(false);
    setAiResult(null);
    setSimilarProducts([]);
    setUploadedS3Url(null);
    sessionStorage.removeItem("imageSearchState");
  }, [previewUrl, originalPreviewUrl]);

  // ─── Camera Handling ────────────────────────────────────────────────

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setCameraMode(false);
  }, [cameraStream]);

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      setCameraStream(stream);
      setCameraMode(true);
      // Attach stream to video element after render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error("Camera access failed:", err);
      if (err.name === "NotAllowedError") {
        setError("Camera access was denied. Please allow camera permissions.");
      } else if (err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else {
        setError("Could not access camera. Please try uploading an image instead.");
      }
    }
  }, [facingMode]);

  const switchCamera = useCallback(async () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    const newFacing = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newFacing);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacing, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera switch failed:", err);
    }
  }, [cameraStream, facingMode]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          stopCamera();
          handleFileSelect(file);
        }
      },
      "image/jpeg",
      0.92
    );
  }, [stopCamera, handleFileSelect]);

  // Cleanup camera on unmount
  React.useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  // Restore state from sessionStorage on mount
  React.useEffect(() => {
    const savedState = sessionStorage.getItem("imageSearchState");
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.previewUrl) setPreviewUrl(parsed.previewUrl);
        if (parsed.originalPreviewUrl) setOriginalPreviewUrl(parsed.originalPreviewUrl);
        if (parsed.aiResult) setAiResult(parsed.aiResult);
        if (parsed.similarProducts) setSimilarProducts(parsed.similarProducts);
        if (parsed.hasSearched) setHasSearched(parsed.hasSearched);
        if (parsed.uploadedS3Url) setUploadedS3Url(parsed.uploadedS3Url);
      } catch (e) {
        sessionStorage.removeItem("imageSearchState");
      }
    }
  }, []);

  // ─── Search ─────────────────────────────────────────────────────────

  const handleSearch = async () => {
    if (!selectedFile || loading) return;

    setLoading(true);
    setError(null);
    setHasSearched(false);
    setAiResult(null);
    setSimilarProducts([]);

    try {
      // Build form data — same file sent to both endpoints
      const formData1 = new FormData();
      formData1.append("image", selectedFile);

      const formData2 = new FormData();
      formData2.append("image", selectedFile);

      // Call both endpoints in parallel via middleman backend
      const [aiModeRes, similarRes] = await Promise.all([
        axios.post(`${config.backendUrl}/image-search/ai_mode`, formData1, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
        axios.post(
          `${config.backendUrl}/image-search/search_similar`,
          formData2,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        ),
      ]);

      // Parse AI mode result
      let parsedAiResult = null;
      if (aiModeRes.data?.ai_response) {
        parsedAiResult = parseAiResponse(aiModeRes.data.ai_response);
        setAiResult(parsedAiResult);
      }

      if (aiModeRes.data?.image_url) {
        setUploadedS3Url(aiModeRes.data.image_url);
        logUserAction({
          action: "image_search",
          data: { image_url: aiModeRes.data.image_url },
        });
      }

      // Set similar products
      if (similarRes.data?.products) {
        setSimilarProducts(similarRes.data.products);
      }

      setHasSearched(true);

      // Clear the localStorage query so that the back button from ProductPage
      // uses navigate(-1) instead of going to /search
      localStorage.removeItem("query");

      // Save complete state to sessionStorage
      const stateToSave = {
        previewUrl: previewUrl,
        originalPreviewUrl: originalPreviewUrl,
        hasSearched: true,
        aiResult: parsedAiResult,
        similarProducts: similarRes.data?.products || [],
        uploadedS3Url: aiModeRes.data?.image_url || null
      };
      sessionStorage.setItem("imageSearchState", JSON.stringify(stateToSave));

    } catch (err) {
      console.error("Image search failed:", err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const trackingContext = React.useMemo(() => ({
    source: "image_search_similar",
    query: "",
    category: "",
  }), []);

  React.useEffect(() => {
    if (similarProducts && similarProducts.length > 0) {
      import("../../utils/analytics").then(({ logBatchUserAction }) => {
        const uniqueBrands = [...new Set(similarProducts.map(p => p.brand).filter(Boolean))];
        if (uniqueBrands.length > 0) {
          logBatchUserAction(uniqueBrands.map(brand => ({
            action: "brand_impression",
            data: {
              brand: brand,
              ...trackingContext
            }
          })));
        }
      });
    }
  }, [similarProducts, trackingContext]);

  // ─── Render ─────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(180deg, #FFFFFF 0%, #FFF9F6 40%, #FFF4EE 70%, #FFFFFF 100%)",
          px: { xs: 2.5, sm: 3, md: 4 },
          pt: { xs: 5, sm: 6, md: 7 },
          pb: { xs: 4, sm: 5, md: 5 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-20%",
            left: "50%",
            transform: "translateX(-50%)",
            width: { xs: "500px", md: "800px" },
            height: { xs: "500px", md: "800px" },
            background:
              "radial-gradient(circle, rgba(255,155,128,0.12) 0%, rgba(255,183,156,0.05) 40%, rgba(255,183,156,0) 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-10%",
            right: "-5%",
            width: { xs: "250px", md: "400px" },
            height: { xs: "250px", md: "400px" },
            background:
              "radial-gradient(circle, rgba(255,107,0,0.06) 0%, rgba(255,107,0,0) 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          },
        }}
      >
        {/* Back Button */}
        <Box
          onClick={() => navigate("/")}
          sx={{
            position: "absolute",
            top: { xs: 16, md: 24 },
            left: { xs: 16, md: 24 },
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: "#fff",
            border: "1px solid #E5E7EB",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#FF6B00",
              color: "#FF6B00",
              boxShadow: "0 4px 12px rgba(255,107,0,0.1)",
              transform: "translateY(-1px)",
            }
          }}
        >
          <ArrowBack sx={{ fontSize: "20px" }} />
        </Box>

        <Box
          sx={{
            maxWidth: "750px",
            mx: "auto",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Icon Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: "56px", sm: "64px" },
                height: { xs: "56px", sm: "64px" },
                borderRadius: "20px",
                background: "linear-gradient(135deg, #FFB79C 0%, #FF6B00 100%)",
                boxShadow: "0 10px 30px rgba(255,107,0,0.25), 0 0 0 6px rgba(255,107,0,0.06)",
                mb: 3,
              }}
            >
              <CameraAlt sx={{ color: "#fff", fontSize: { xs: "28px", sm: "32px" } }} />
            </Box>

            {/* Title */}
            <Typography
              variant="h1"
              sx={{
                fontFamily: "'Lora', serif",
                fontSize: { xs: "32px", sm: "42px", md: "52px" },
                color: "#111",
                mb: 2,
                fontWeight: 500,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              Image <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>Search</Box>
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                fontFamily: "Poppins-Medium",
                fontSize: { xs: "14px", sm: "15px", md: "17px" },
                color: "#555",
                maxWidth: "580px",
                mx: "auto",
                lineHeight: 1.7,
                mb: { xs: 4, sm: 5 },
              }}
            >
              Upload a photo or screenshot of any item you love. We'll identify it and also find you similar products across our catalog.
            </Typography>
          </motion.div>

          {/* ─── How It Works: 3-Step Guide ─── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              sx={{
                display: { xs: "flex", sm: "grid" },
                flexDirection: { xs: "column", sm: "unset" },
                gridTemplateColumns: { sm: "minmax(0, 1fr) auto auto auto minmax(0, 1fr)" },
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.5, sm: 0 },
                mb: 1,
              }}
            >
              {[
                { icon: <CameraAlt sx={{ fontSize: "18px" }} />, label: "Upload Product Photo", delay: 0.4 },
                { icon: <AutoAwesome sx={{ fontSize: "18px" }} />, label: "AI Identifies", delay: 0.55 },
                { icon: <ShoppingBag sx={{ fontSize: "18px" }} />, label: "Find The Product & Similar Items", delay: 0.7 },
              ].map((step, idx) => (
                <React.Fragment key={idx}>
                  <motion.div
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent:
                        idx === 0 ? "flex-end" :
                          idx === 2 ? "flex-start" : "center"
                    }}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: step.delay, ease: "easeOut" }}
                  >
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        px: { xs: 2, sm: 1.5, md: 2 },
                        py: { xs: 1.25, sm: 1.25 },
                        borderRadius: "100px",
                        backgroundColor: "#fff",
                        border: "1px solid rgba(255,107,0,0.15)",
                        boxShadow: "0 2px 8px rgba(255,107,0,0.06)",
                        color: "#FF6B00",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 16px rgba(255,107,0,0.12)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {step.icon}
                      <Typography
                        sx={{
                          fontFamily: "Poppins-Medium",
                          fontSize: { xs: "12px", sm: "11px", md: "13px" },
                          color: "#333",
                          whiteSpace: { xs: "nowrap", sm: "normal", md: "nowrap" },
                          textAlign: "center",
                          lineHeight: 1.2,
                        }}
                      >
                        {step.label}
                      </Typography>
                    </Box>
                  </motion.div>
                  {/* Arrow connector (not after last) */}
                  {idx < 2 && (
                    <ArrowForward
                      sx={{
                        fontSize: "18px",
                        color: "rgba(255,107,0,0.3)",
                        mx: { xs: 0, sm: 0.5, md: 1 },
                        my: { xs: 0.25, sm: 0 },
                        transform: { xs: "rotate(90deg)", sm: "none" },
                        display: "block",
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          maxWidth: "1200px",
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
        }}
      >
        {/* Upload Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Box
            sx={{
              maxWidth: "600px",
              mx: "auto",
              mb: 4,
            }}
          >
            {!previewUrl && !cameraMode ? (
              /* Option Tiles: Upload or Camera */
              <Box>
                {/* Drop zone */}
                <Box
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: "2px dashed",
                    borderColor: isDragging ? "#FF6B00" : "rgba(255,107,0,0.25)",
                    borderRadius: "24px",
                    p: { xs: 3, sm: 4, md: 5 },
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: isDragging
                      ? "rgba(255,107,0,0.04)"
                      : "#FFFAF7",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    mb: 2,
                    position: "relative",
                    overflow: "hidden",
                    "@keyframes borderPulse": {
                      "0%, 100%": { borderColor: "rgba(255,107,0,0.15)" },
                      "50%": { borderColor: "rgba(255,107,0,0.4)" },
                    },
                    animation: isDragging ? "none" : "borderPulse 3s ease-in-out infinite",
                    "&:hover": {
                      borderColor: "#FF6B00",
                      backgroundColor: "rgba(255,107,0,0.04)",
                      transform: "translateY(-3px)",
                      boxShadow: "0 12px 32px rgba(255,107,0,0.08)",
                      animation: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "64px", sm: "72px" },
                      height: { xs: "64px", sm: "72px" },
                      borderRadius: "20px",
                      background: "linear-gradient(135deg, rgba(255,155,128,0.2) 0%, rgba(255,107,0,0.12) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2.5,
                    }}
                  >
                    <CloudUpload sx={{ fontSize: { xs: "28px", sm: "32px" }, color: "#FF6B00" }} />
                  </Box>

                  <Typography
                    sx={{
                      fontFamily: "Poppins-SemiBold",
                      fontSize: { xs: "16px", sm: "18px" },
                      fontWeight: 600,
                      color: "#1a1a1a",
                      mb: 0.75,
                    }}
                  >
                    {isDragging ? "Drop your image here" : "Upload a product image"}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Poppins-Regular",
                      fontSize: { xs: "13px", sm: "14px" },
                      color: "#999",
                      mb: 1.5,
                    }}
                  >
                    Drag & drop or click to browse
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Poppins-Regular",
                      fontSize: "12px",
                      color: "#bbb",
                    }}
                  >
                    JPEG, PNG, or WebP • Max 15MB
                  </Typography>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={(e) => {
                      handleFileSelect(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </Box>

                {/* Divider */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    my: 2,
                  }}
                >
                  <Box sx={{ flex: 1, height: "1px", background: "linear-gradient(90deg, transparent, rgba(255,107,0,0.15), transparent)" }} />
                  <Typography
                    sx={{
                      fontFamily: "Poppins-Medium",
                      fontSize: "11px",
                      color: "#ccc",
                      textTransform: "uppercase",
                      letterSpacing: "2px",
                    }}
                  >
                    or
                  </Typography>
                  <Box sx={{ flex: 1, height: "1px", background: "linear-gradient(90deg, transparent, rgba(255,107,0,0.15), transparent)" }} />
                </Box>

                {/* Camera Button */}
                <Box
                  onClick={() => {
                    if (smallScreen) {
                      cameraInputRef.current?.click();
                    } else {
                      startCamera();
                    }
                  }}
                  sx={{
                    border: "2px solid #E5E7EB",
                    borderRadius: "20px",
                    p: { xs: 2.5, sm: 3 },
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: "#FAFAFA",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    "&:hover": {
                      borderColor: "#9AE0EB",
                      backgroundColor: "rgba(154,224,235,0.04)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                      "& .camera-icon-box": {
                        background:
                          "linear-gradient(135deg, #9AE0EB 0%, #7DD3E0 100%)",
                        "& svg": { color: "#fff" },
                      },
                    },
                  }}
                >
                  <Box
                    className="camera-icon-box"
                    sx={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "14px",
                      background:
                        "linear-gradient(135deg, rgba(154,224,235,0.15) 0%, rgba(125,211,224,0.15) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                      flexShrink: 0,
                    }}
                  >
                    <PhotoCamera
                      sx={{
                        fontSize: "24px",
                        color: "#7DD3E0",
                        transition: "color 0.3s ease",
                      }}
                    />
                  </Box>

                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      sx={{
                        fontFamily: "Poppins-SemiBold",
                        fontSize: { xs: "15px", sm: "16px" },
                        fontWeight: 600,
                        color: "#1a1a1a",
                        lineHeight: 1.3,
                      }}
                    >
                      Take a Photo
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Poppins-Regular",
                        fontSize: "13px",
                        color: "#999",
                        lineHeight: 1.4,
                      }}
                    >
                      Use your camera to capture a product
                    </Typography>
                  </Box>
                </Box>

                {/* Hidden camera file input for mobile */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    handleFileSelect(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </Box>
            ) : cameraMode ? (
              /* Camera Live Preview */
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "24px",
                  overflow: "hidden",
                  border: "2px solid rgba(255,155,128,0.4)",
                  backgroundColor: "#000",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: "100%",
                    maxHeight: "450px",
                    objectFit: "cover",
                    display: "block",
                    transform: facingMode === "user" ? "scaleX(-1)" : "none",
                  }}
                />
                <canvas ref={canvasRef} style={{ display: "none" }} />

                {/* Camera controls overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                    py: 2.5,
                    background:
                      "linear-gradient(0deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)",
                  }}
                >
                  {/* Cancel */}
                  <Box
                    onClick={stopCamera}
                    sx={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.35)",
                      },
                    }}
                  >
                    <Close sx={{ color: "#fff", fontSize: "22px" }} />
                  </Box>

                  {/* Capture button */}
                  <Box
                    onClick={capturePhoto}
                    sx={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "50%",
                      border: "4px solid #fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.08)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #FFB79C 0%, #FF9B80 100%)",
                        transition: "all 0.2s ease",
                      }}
                    />
                  </Box>

                  {/* Flip camera */}
                  <Box
                    onClick={switchCamera}
                    sx={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.35)",
                      },
                    }}
                  >
                    <FlipCameraIos sx={{ color: "#fff", fontSize: "22px" }} />
                  </Box>
                </Box>
              </Box>
            ) : (
              /* Image Preview */
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "24px",
                  overflow: "hidden",
                  border: "1.5px solid rgba(255,107,0,0.15)",
                  backgroundColor: "#FFFAF7",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                }}
              >
                <img
                  src={previewUrl}
                  alt="Upload preview"
                  style={{
                    width: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                    display: "block",
                  }}
                />

                {/* Crop & Remove buttons */}
                {!loading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "12px",
                      right: "12px",
                      display: "flex",
                      gap: 1
                    }}
                  >
                    {!hasSearched && (
                      <Box
                        onClick={() => setIsCropping(true)}
                        sx={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(0,0,0,0.5)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.7)",
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <Crop sx={{ color: "#fff", fontSize: "18px" }} />
                      </Box>
                    )}
                    <Box
                      onClick={removeFile}
                      sx={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.7)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <Close sx={{ color: "#fff", fontSize: "20px" }} />
                    </Box>
                  </Box>
                )}

                {/* File name bar */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    backgroundColor: "#fff",
                    borderTop: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Poppins-Medium",
                      fontSize: "13px",
                      color: "#666",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "70%",
                    }}
                  >
                    {selectedFile?.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Poppins-Regular",
                      fontSize: "12px",
                      color: "#999",
                    }}
                  >
                    {(selectedFile?.size / 1024).toFixed(0)} KB
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Crop Dialog */}
            <Dialog
              open={isCropping}
              onClose={() => setIsCropping(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: { borderRadius: "16px", p: 1 }
              }}
            >
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: "18px", color: "#1a1a1a" }}>
                    Crop Image
                  </Typography>
                  <Typography sx={{ fontFamily: "Poppins-Regular", fontSize: "14px", color: "#666", mt: 0.5 }}>
                    Adjust the corners of the box to frame the product.
                  </Typography>
                </Box>

                <Box sx={{ overflow: "auto", maxHeight: "60vh", textAlign: "center", backgroundColor: "#f5f5f5", borderRadius: "8px", p: 2 }}>
                  {originalPreviewUrl && (
                    <ReactCrop
                      crop={crop}
                      onChange={(_, percentCrop) => setCrop(percentCrop)}
                      onComplete={(c) => setCompletedCrop(c)}
                    >
                      <img
                        ref={imgRef}
                        src={originalPreviewUrl}
                        alt="Crop preview"
                        style={{ maxHeight: "55vh", maxWidth: "100%", objectFit: "contain" }}
                        onLoad={(e) => {
                          if (!crop) {
                            const { width, height } = e.currentTarget;
                            const initialCrop = {
                              unit: 'px',
                              width: width,
                              height: height,
                              x: 0,
                              y: 0
                            };
                            setCrop(initialCrop);
                            setCompletedCrop(initialCrop);
                          }
                        }}
                      />
                    </ReactCrop>
                  )}
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
                  <Button
                    onClick={() => setIsCropping(false)}
                    sx={{
                      color: "#666",
                      fontFamily: "Poppins-Medium",
                      textTransform: "none",
                      borderRadius: "10px",
                      px: 3
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCropApply}
                    variant="contained"
                    sx={{
                      background: "linear-gradient(135deg, #FF6B00 0%, #FF9B80 100%)",
                      color: "#fff",
                      fontFamily: "Poppins-Medium",
                      textTransform: "none",
                      borderRadius: "100px",
                      px: 4,
                      boxShadow: "0 4px 12px rgba(255,107,0,0.25)",
                      "&:hover": {
                        boxShadow: "0 6px 16px rgba(255,107,0,0.35)",
                      }
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            </Dialog>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <Typography
                    sx={{
                      mt: 2,
                      fontFamily: "Poppins-Medium",
                      fontSize: "13px",
                      color: "#e74c3c",
                      textAlign: "center",
                    }}
                  >
                    {error}
                  </Typography>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Search Button */}
            {selectedFile && !hasSearched && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <Box
                  onClick={handleSearch}
                  sx={{
                    mt: 3,
                    mx: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    px: 4,
                    py: 1.5,
                    borderRadius: "100px",
                    background: loading
                      ? "#E5E7EB"
                      : "linear-gradient(135deg, #FF6B00 0%, #FF9B80 100%)",
                    color: "#fff",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: "Poppins-SemiBold",
                    fontSize: { xs: "15px", sm: "16px" },
                    fontWeight: 600,
                    boxShadow: loading
                      ? "none"
                      : "0 6px 20px rgba(255,107,0,0.3)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    maxWidth: "280px",
                    position: "relative",
                    overflow: "hidden",
                    "@keyframes searchShine": {
                      "0%": { left: "-100%" },
                      "100%": { left: "200%" },
                    },
                    "&::after": loading ? {} : {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "60%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)",
                      animation: "searchShine 2.5s ease-in-out 1s infinite",
                    },
                    "&:hover": {
                      transform: loading ? "none" : "translateY(-2px)",
                      boxShadow: loading
                        ? "none"
                        : "0 8px 28px rgba(255,107,0,0.4)",
                    },
                    "&:active": {
                      transform: loading ? "none" : "translateY(0)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: "#999" }} />
                  ) : (
                    <Search sx={{ fontSize: "22px" }} />
                  )}
                  {loading ? "Searching" + dots : "Search"}
                </Box>
              </motion.div>
            )}
          </Box>
        </motion.div>

        {/* Loading State */}
        <AnimatePresence>
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  py: 4,
                }}
              >
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#FF6B00",
                    animation: "searchPulse 1.5s ease-in-out infinite",
                    "@keyframes searchPulse": {
                      "0%, 100%": { opacity: 0.5 },
                      "50%": { opacity: 1 },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Poppins-Medium",
                    fontSize: "15px",
                    color: "#6B7280",
                  }}
                >
                  Analyzing your image and finding products{dots}
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Results ─────────────────────────────────────────────────── */}
        {hasSearched && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* AI Mode Identified Product Card */}
            {aiResult && (() => {
              const rawLower = aiResult.raw?.toLowerCase() || "";
              const nameLower = aiResult.productName?.toLowerCase() || "";
              const isNotFound =
                rawLower.includes("not found") ||
                nameLower.includes("not found") ||
                rawLower.includes("no response available") ||
                nameLower.includes("no response available");

              if (isNotFound) {
                return (
                  <Box
                    sx={{
                      mb: 4,
                      p: { xs: 3, sm: 5 },
                      textAlign: "center",
                      backgroundColor: "rgba(255,183,156,0.05)",
                      borderRadius: "20px",
                      border: "1px dashed rgba(255,183,156,0.4)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: "72px",
                        height: "72px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, rgba(255,107,0,0.15) 0%, rgba(255,183,156,0.15) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(255,107,0,0.1)",
                      }}
                    >
                      <SearchOff sx={{ fontSize: "36px", color: "#FF6B00" }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: "Poppins-SemiBold",
                          fontSize: "18px",
                          color: "#1a1a1a",
                          mb: 1,
                        }}
                      >
                        Product Could Not Be Identified
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Poppins-Regular",
                          fontSize: "14px",
                          color: "#666",
                          maxWidth: "450px",
                          mx: "auto",
                          lineHeight: 1.6,
                        }}
                      >
                        You can try cropping the image or uploading another picture.
                      </Typography>
                    </Box>
                  </Box>
                );
              }

              return (
                <Box sx={{ mb: 5 }}>
                  <Typography
                    sx={{
                      fontFamily: "'Lora', serif",
                      fontSize: { xs: "22px", sm: "26px", md: "30px" },
                      fontWeight: 500,
                      color: "#111",
                      letterSpacing: "-0.01em",
                      mb: 2.5,
                    }}
                  >
                    Identified <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>Product</Box>
                  </Typography>

                  <Box
                    onClick={() => {
                      if (aiResult.linkUrl) {
                        logUserAction({
                          action: "image_search_buy_click",
                          data: {
                            product_title: aiResult.productName || null,
                            brand: aiResult.brand || null,
                            product_url: aiResult.linkUrl,
                            product_image: previewUrl,
                            s3_image_url: uploadedS3Url,
                          }
                        });
                        const outboundUrl = appendUtmTags(aiResult.linkUrl);
                        window.open(outboundUrl, "_blank", "noopener,noreferrer");
                      }
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      borderRadius: "20px",
                      overflow: "hidden",
                      border: "1px solid rgba(255,155,128,0.2)",
                      backgroundColor: "rgba(255,250,247,0.8)",
                      backdropFilter: "blur(10px)",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      cursor: aiResult.linkUrl ? "pointer" : "default",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": aiResult.linkUrl
                        ? {
                          boxShadow: "0 12px 32px rgba(255,107,0,0.1)",
                          transform: "translateY(-4px)",
                          borderColor: "rgba(255,155,128,0.4)",
                        }
                        : {},
                      maxWidth: { xs: "100%", sm: "600px" },
                    }}
                  >
                    {/* Card Image */}
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "240px" },
                        minHeight: { xs: "280px", sm: "320px" },
                        flexShrink: 0,
                        backgroundColor: "#fafafa",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={previewUrl}
                        alt="Uploaded product"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>

                    {/* Card Content */}
                    <Box
                      sx={{
                        flex: 1,
                        p: { xs: 2.5, sm: 3 },
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                        backgroundColor: "transparent",
                      }}
                    >
                      {/* Brand */}
                      {aiResult.brand && (
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontFamily: "Poppins-Medium",
                            fontWeight: 500,
                            color: "#999",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                          }}
                        >
                          {aiResult.brand}
                        </Typography>
                      )}

                      {/* Product Name */}
                      {aiResult.productName && (
                        <Typography
                          sx={{
                            fontSize: { xs: "18px", sm: "20px" },
                            fontFamily: "'Lora', serif",
                            fontWeight: 500,
                            color: "#1a1a1a",
                            lineHeight: 1.3,
                          }}
                        >
                          {aiResult.productName}
                        </Typography>
                      )}

                      {/* Description */}
                      {aiResult.description && (
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Poppins-Regular",
                            color: "#666",
                            lineHeight: 1.6,
                            mt: 0.5,
                          }}
                        >
                          {aiResult.description}
                        </Typography>
                      )}

                      {/* Link CTA */}
                      {aiResult.linkUrl && (
                        <Box
                          sx={{
                            mt: "auto",
                            pt: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              px: 2.5,
                              py: 1,
                              borderRadius: "100px",
                              background:
                                "linear-gradient(135deg, #FF6B00 0%, #FF9B80 100%)",
                              color: "#fff",
                              fontFamily: "Poppins-Medium",
                              fontSize: "13px",
                              fontWeight: 500,
                              boxShadow: "0 2px 8px rgba(255,107,0,0.3)",
                              transition: "all 0.2s ease",
                            }}
                          >
                            View Product →
                          </Box>
                        </Box>
                      )}

                      {/* Fallback: show raw markdown if parsing got nothing useful */}
                      {!aiResult.productName &&
                        !aiResult.brand &&
                        aiResult.raw && (
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontFamily: "Poppins-Regular",
                              color: "#444",
                              lineHeight: 1.6,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {aiResult.raw}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                </Box>
              );
            })()}

            {/* Similar Products */}
            {similarProducts.length > 0 && (
              <Box sx={{ mb: 5 }}>
                <Typography
                  sx={{
                    fontFamily: "'Lora', serif",
                    fontSize: { xs: "22px", sm: "26px", md: "30px" },
                    fontWeight: 500,
                    color: "#111",
                    letterSpacing: "-0.01em",
                    mb: 2.5,
                  }}
                >
                  Similar <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>Products</Box>
                </Typography>

                <ImageSimilarProductCard
                  productData={similarProducts}
                  noColor
                  oneLiner
                  index={1}
                  showAll={true}
                  verticalLayout={!smallScreen}
                  scrollKey="imageSearchSimilar"
                  trackingContext={trackingContext}
                />
              </Box>
            )}

            {/* No results */}
            {!aiResult && similarProducts.length === 0 && (
              <Box
                sx={{
                  borderRadius: "16px",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                  border: "1px solid #E5E7EB",
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins-SemiBold",
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#1a1a1a",
                    mb: 1,
                  }}
                >
                  No products found
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins-Regular",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  We couldn't identify the product. Try uploading another
                  image.
                </Typography>
              </Box>
            )}

            {/* Search again button */}
            <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
              <Box
                onClick={removeFile}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 3.5,
                  py: 1.5,
                  borderRadius: "100px",
                  border: "1.5px solid rgba(255,107,0,0.2)",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  fontFamily: "Poppins-Medium",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#666",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#FF9B80",
                    color: "#FF6B00",
                    backgroundColor: "rgba(255,107,0,0.03)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(255,107,0,0.08)",
                  },
                }}
              >
                <CameraAlt sx={{ fontSize: "18px" }} />
                Search with another image
              </Box>
            </Box>
          </motion.div>
        )}
      </Box>

      <FloatingSearchBar />
      <Footer />
    </Box>
  );
};

export default ImageSearch;
